// Sprint B+: Discussion, Announcement, Chat, Analytics, Anti-piracy frontend components.
import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageSquare, Send, Pin, ArrowUp, Trash2, Megaphone, Plus, BarChart3, TrendingUp, ShieldAlert, Bookmark, Users as UsersIcon } from "lucide-react";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF0066]/40";

// ============= DISCUSSION =============
export function DiscussionThread({ classId, classTitle }) {
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/classes/${classId}/discussion`); setPosts(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, classId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/classes/${classId}/discussion`, { body, parent_id: replyTo });
      setBody(""); setReplyTo(null); load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const upvote = async (id) => { await api.post(`/discussion/${id}/upvote`).catch(() => {}); load(); };
  const pin = async (id) => { await api.post(`/discussion/${id}/pin`).catch(() => {}); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/discussion/${id}`).catch(() => toast.error("Failed")); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost !py-1.5 text-xs" title="Discussion" data-testid={`discussion-${classId}`}>
          <MessageSquare className="w-3.5 h-3.5" /> Doubts{posts.length > 0 ? ` (${posts.length})` : ""}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#FF0066]" /> Discussion</DialogTitle>
          <div className="text-xs text-white/50">{classTitle}</div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-2 rounded-lg bg-white/[0.03] border border-white/10 p-3">
          {replyTo && <div className="text-[10px] text-[#FF0066] flex items-center gap-1">Replying… <button type="button" onClick={() => setReplyTo(null)} className="underline">cancel</button></div>}
          <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={replyTo ? "Your reply…" : "Ask a doubt…"} className={inp} data-testid="discussion-input" />
          <button disabled={busy || !body.trim()} className="bg-[#FF0066] hover:bg-[#e6005c] text-white text-xs font-semibold rounded-full px-4 py-1.5 disabled:opacity-60" data-testid="discussion-post-btn">
            <Send className="w-3 h-3 inline mr-1" /> {busy ? "Posting…" : "Post"}
          </button>
        </form>

        <div className="space-y-3 mt-3">
          {posts.length === 0 && <div className="text-sm text-white/40 text-center py-6">No questions yet. Be the first!</div>}
          {posts.map((p) => (
            <div key={p.id} className={`rounded-lg border ${p.pinned ? "border-amber-400/40 bg-amber-500/5" : "border-white/10 bg-white/[0.02]"} p-3 space-y-2`} data-testid={`post-${p.id}`}>
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-[#FF0066]/20 flex items-center justify-center text-[10px] font-bold shrink-0">{p.user_name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                    {p.user_name}
                    <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-white/5 text-white/60">{p.user_role}</span>
                    {p.pinned && <span className="text-[9px] text-amber-300 inline-flex items-center gap-0.5"><Pin className="w-2.5 h-2.5" /> Pinned</span>}
                    {p.is_answer && <span className="text-[9px] text-emerald-300">✓ Answer</span>}
                  </div>
                  <div className="text-sm text-white/85 mt-0.5 whitespace-pre-line">{p.body}</div>
                  <div className="mt-1.5 flex items-center gap-2 text-[10px] text-white/40">
                    <button onClick={() => upvote(p.id)} className="inline-flex items-center gap-0.5 hover:text-[#FF0066]" data-testid={`upvote-${p.id}`}><ArrowUp className="w-3 h-3" /> {p.upvote_count || 0}</button>
                    <button onClick={() => setReplyTo(p.id)} className="hover:text-[#FF0066]" data-testid={`reply-${p.id}`}>Reply</button>
                    <button onClick={() => pin(p.id)} className="hover:text-amber-300" data-testid={`pin-${p.id}`}>{p.pinned ? "Unpin" : "Pin"}</button>
                    <button onClick={() => del(p.id)} className="hover:text-red-300 ml-auto"><Trash2 className="w-3 h-3" /></button>
                    <span>{new Date(p.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              {(p.replies || []).length > 0 && (
                <div className="ml-9 space-y-1.5 border-l border-white/10 pl-3">
                  {p.replies.map((r) => (
                    <div key={r.id} className="text-xs" data-testid={`reply-row-${r.id}`}>
                      <span className="font-semibold">{r.user_name}</span>
                      <span className="text-[9px] uppercase ml-1 px-1 py-0.5 rounded bg-white/5 text-white/60">{r.user_role}</span>
                      <div className="text-white/75 mt-0.5 whitespace-pre-line">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============= ANNOUNCEMENT BROADCAST =============
export function AnnouncementBroadcast() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [audiences, setAudiences] = useState({ batches: [], courses: [], roles: [] });
  const empty = { title: "", body: "", audience_kind: "all", audience_id: "", send_email: true };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/announcements").then(({ data }) => setList(data)).catch(() => {});
    api.get("/admin/announcements/audiences").then(({ data }) => setAudiences(data)).catch(() => {});
  };
  useEffect(() => { if (open) load(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announcements", f);
      toast.success(`Sent to ${data.recipient_count} users` + (data.email_sent ? ` (${data.emails_delivered || 0} emails)` : ""));
      setF(empty); load();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/announcements/${id}`).catch(() => toast.error("Failed")); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary text-sm" data-testid="open-announcement">
          <Megaphone className="w-4 h-4" /> Broadcast
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#FF0066]" /> Announcement broadcast</DialogTitle>
          <div className="text-xs text-white/50">Send to all / role / batch / course. Creates in-app notification + optional email.</div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <label className="block"><div className="text-xs text-white/50 mb-1">Title</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} data-testid="ann-title" /></label>
          <label className="block"><div className="text-xs text-white/50 mb-1">Body</div><textarea rows={4} required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={inp} data-testid="ann-body" /></label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/50 mb-1">Audience</div>
              <select value={f.audience_kind} onChange={(e) => setF({ ...f, audience_kind: e.target.value, audience_id: "" })} className={inp} data-testid="ann-audience">
                <option value="all">🌐 All students</option>
                <option value="role">👥 By role</option>
                <option value="batch">📅 Specific batch</option>
                <option value="course">📚 Specific course</option>
              </select>
            </label>
            {f.audience_kind !== "all" && (
              <label className="block">
                <div className="text-xs text-white/50 mb-1">Pick {f.audience_kind}</div>
                <select required value={f.audience_id} onChange={(e) => setF({ ...f, audience_id: e.target.value })} className={inp} data-testid="ann-audience-id">
                  <option value="">— select —</option>
                  {f.audience_kind === "role" && audiences.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  {f.audience_kind === "batch" && audiences.batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  {f.audience_kind === "course" && audiences.courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </label>
            )}
          </div>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} /> Also send email blast</label>
          <button disabled={busy} className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-5 py-2 text-sm disabled:opacity-60 w-full" data-testid="ann-submit">{busy ? "Sending…" : "Send announcement"}</button>
        </form>

        <div className="mt-5 border-t border-white/10 pt-4 space-y-2">
          <div className="text-xs text-white/50 mb-1">Recent announcements ({list.length})</div>
          {list.slice(0, 10).map((a) => (
            <div key={a.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 text-xs flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{a.title}</div>
                <div className="text-white/50 line-clamp-2 mt-0.5">{a.body}</div>
                <div className="text-[10px] text-white/40 mt-1">{new Date(a.created_at).toLocaleString()} • {a.audience_kind}{a.audience_id ? `:${a.audience_id}` : ""} • {a.recipient_count} recipients</div>
              </div>
              <button onClick={() => del(a.id)} className="p-1 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============= CHAT =============
export function ChatTab() {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/chat/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/chat/unread-count").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, []);

  const open = async (c) => {
    setSelected(c);
    try { const { data } = await api.get(`/chat/${c.id}/messages`); setMsgs(data); setUnread(0); }
    catch { toast.error("Load failed"); }
  };

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !selected) return;
    try {
      const { data } = await api.post(`/chat/${selected.id}/messages`, { body });
      setMsgs((m) => [...m, data]); setBody("");
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="chat-tab">
      <div className="grid grid-cols-12 h-[60vh]">
        <div className="col-span-4 border-r border-white/10 overflow-y-auto">
          <div className="p-3 text-xs text-fuchsia-300 uppercase tracking-wider font-bold">Contacts ({contacts.length}){unread > 0 && <span className="ml-2 text-[10px] bg-[#FF0066] text-white rounded-full px-1.5">{unread}</span>}</div>
          {contacts.length === 0 && <div className="px-3 py-4 text-xs text-white/40">No contacts available.</div>}
          {contacts.map((c) => (
            <button key={c.id} onClick={() => open(c)} className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5 ${selected?.id === c.id ? "bg-[#FF0066]/10" : ""}`} data-testid={`chat-contact-${c.id}`}>
              <div className="w-8 h-8 rounded-full bg-[#FF0066]/20 flex items-center justify-center text-[11px] font-bold shrink-0">{c.profile_image ? <img src={c.profile_image} className="w-full h-full rounded-full object-cover" /> : c.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate">{c.name}</div>
                <div className="text-[10px] text-white/50">{c.role}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="col-span-8 flex flex-col">
          {!selected ? <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Select a contact to start chatting…</div> : (
            <>
              <div className="px-3 py-2.5 border-b border-white/10 text-sm font-semibold">{selected.name} <span className="text-[10px] text-white/40 uppercase ml-1">{selected.role}</span></div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
                {msgs.length === 0 && <div className="text-center text-white/30 text-xs py-6">Say hi! 👋</div>}
                {msgs.map((m) => {
                  const own = m.from_user_id !== selected.id;
                  return (
                    <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-xs ${own ? "bg-[#FF0066] text-white" : "bg-white/10 text-white"}`}>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <div className={`text-[9px] mt-0.5 ${own ? "text-white/70" : "text-white/40"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-white/10 p-2 flex gap-2">
                <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className={inp + " flex-1"} data-testid="chat-input" />
                <button className="bg-[#FF0066] hover:bg-[#e6005c] text-white rounded-full px-4 text-sm disabled:opacity-60" disabled={!body.trim()} data-testid="chat-send"><Send className="w-3.5 h-3.5" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============= ANALYTICS PANEL =============
export function AnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics/overview").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="text-white/40 py-8 text-sm">Loading analytics…</div>;
  const s = data.stats;
  return (
    <div className="space-y-5" data-testid="analytics-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Students", val: s.total_students, color: "fuchsia" },
          { label: "Classes", val: s.total_classes, color: "purple" },
          { label: "Progress records", val: s.total_progress_records, color: "amber" },
          { label: "Completions", val: s.total_completions, color: "emerald" },
          { label: "Completion %", val: s.completion_rate + "%", color: "rose" },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl bg-${c.color}-500/10 border border-${c.color}-400/20 p-3`}>
            <div className="text-[10px] uppercase text-white/50">{c.label}</div>
            <div className="text-xl font-bold mt-1">{c.val}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-3"><TrendingUp className="w-3.5 h-3.5 inline mr-1" /> Top viewed lectures</div>
          {data.top_lectures.length === 0 ? <div className="text-white/40 text-xs">No data yet (students haven't watched any lectures).</div> :
            data.top_lectures.map((t, i) => (
              <div key={t.class_id} className="py-1.5 border-b border-white/5 last:border-0 text-xs flex items-center gap-2">
                <div className="w-5 text-white/40">{i + 1}.</div>
                <div className="flex-1 truncate">{t.title}</div>
                <div className="text-fuchsia-300 font-semibold">{t.viewers}</div>
                <div className="text-white/50 text-[10px]">{t.avg_percent}%</div>
              </div>
            ))}
        </div>
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-3"><UsersIcon className="w-3.5 h-3.5 inline mr-1" /> Most active (30 days)</div>
          {data.active_students.length === 0 ? <div className="text-white/40 text-xs">No active students yet.</div> :
            data.active_students.map((a, i) => (
              <div key={a.user_id} className="py-1.5 border-b border-white/5 last:border-0 text-xs flex items-center gap-2">
                <div className="w-5 text-white/40">{i + 1}.</div>
                {a.profile_image ? <img src={a.profile_image} className="w-5 h-5 rounded-full object-cover" /> : <div className="w-5 h-5 rounded-full bg-[#FF0066]/20 flex items-center justify-center text-[8px] font-bold">{a.name?.[0]?.toUpperCase()}</div>}
                <div className="flex-1 truncate">{a.name}</div>
                <div className="text-fuchsia-300 font-semibold">{a.lectures_watched}</div>
                <div className="text-white/50 text-[10px]">{a.total_minutes}m</div>
              </div>
            ))}
        </div>
      </div>

      {data.revenue_daily.length > 0 && (
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold mb-3"><BarChart3 className="w-3.5 h-3.5 inline mr-1" /> Revenue last 14 days</div>
          <div className="flex items-end gap-1 h-32">
            {data.revenue_daily.map((d) => {
              const max = Math.max(...data.revenue_daily.map((x) => x.amount));
              const h = Math.max(2, (d.amount / max) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ₹${d.amount.toLocaleString("en-IN")} (${d.count})`}>
                  <div className="text-[9px] text-white/40">₹{(d.amount / 1000).toFixed(0)}k</div>
                  <div className="w-full bg-gradient-to-t from-[#FF0066] to-fuchsia-400 rounded-t" style={{ height: `${h}%` }} />
                  <div className="text-[8px] text-white/40">{d.date.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <FlaggedDevices />
    </div>
  );
}

function FlaggedDevices() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/anti-piracy/flagged").then(({ data }) => setList(data)).catch(() => {}); }, []);
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-amber-300 font-bold mb-3"><ShieldAlert className="w-3.5 h-3.5 inline mr-1" /> Anti-piracy alerts ({list.length})</div>
      {list.length === 0 ? <div className="text-white/40 text-xs">No flagged devices in last 30 days. ✅</div> :
        list.map((u) => (
          <div key={u.user_id} className="py-1.5 border-b border-white/5 last:border-0 text-xs flex items-center gap-2">
            <div className="flex-1 truncate">{u.user_email}</div>
            <div className="text-amber-300 font-semibold">{u.device_count} devices</div>
          </div>
        ))}
    </div>
  );
}

// ============= MY BOOKMARKS (student) =============
export function MyBookmarksTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/my/bookmarks").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2" data-testid="bookmarks-tab">
      {items.length === 0 && <div className="text-white/40 text-sm text-center py-8">No bookmarked lectures yet. Click the <Bookmark className="w-3 h-3 inline" /> icon on any lecture to save it.</div>}
      {items.map((c) => (
        <div key={c.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`bm-${c.id}`}>
          <div className="font-medium text-sm">{c.title}</div>
          <div className="text-[10px] text-white/40">{c.course_title} • Saved {new Date(c.bookmarked_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}
